/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ILinkProps, ILinkStyles } from '@fluentui/react/lib/components/Link';
import { Stack, StackItem } from '@fluentui/react/lib/Stack';
import { HighContrastSelector } from '@fluentui/react/lib/Styling';
import { classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, important } from 'src/core/themes/luisStylist';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { truncateValue } from 'src/core/utils/number';
import { PerformanceMetricsBreakdown } from 'src/modules/modelDetails/stores/ModelEvaluationStore';
import styled from 'styled-components';

export type ConfusionMatrixFormat = 'raw' | 'normalized';
export type ConfusionMatrixMode = 'all' | 'errors' | 'matches';

export type ModelConfusionMatrixProps = {
    mode: ConfusionMatrixMode;
    allViewIndices: number[];
    onlyErrorsIndices: number[];
    onlyMatchesIndices: number[];
    modelConfusionMatrix: number[][];
    confusionMatrixCellColors: {
        cellColor: string;
        textColor: string;
    }[][];
    onCellClick: (selectedLabels: string[]) => void;
    performanceMetricsBreakdown: PerformanceMetricsBreakdown[];
};

const messages = defineMessages({
    confusionMatrixAriaLabel: {
        id: 'modelGuidance.modelConfusionMatrix.confusionMatrixAriaLabel',
        defaultMessage: 'Confusion Matrix'
    },
    labeledAs: {
        id: 'modelGuidance.modelConfusionMatrix.labeledAs',
        defaultMessage: 'Labeled as'
    },
    predictedAs: {
        id: 'modelGuidance.modelConfusionMatrix.predictedAs',
        defaultMessage: 'Predicted as'
    },
    matchCellAriaLabel: {
        id: 'modelGuidance.modelConfusionMatrix.matchCellAriaLabel',
        defaultMessage: 'Match cell'
    },
    CellValueAriaLabel: {
        id: 'modelGuidance.modelConfusionMatrix.cellValueAriaLabel',
        defaultMessage: 'Value is {value}.'
    }
});

const theme = getScopedTheme('ModelGuidanceConfusionMatrix');

const basicTextContainerStyles = { height: '48px', display: 'flex', alignItems: 'center' };
const StyledLabel = styled.div(() => ({
    ...basicTextContainerStyles,
    ...{ width: '72px', justifyContent: 'center', border: theme.border, marginLeft: '0px' }
}));
const StyledLabelText = styled(TruncatedText)`
    whitespace: nowrap;
    min-width: 54px;
    text-align: center;
`;
const StyledPrediction = styled.div(() => ({
    ...basicTextContainerStyles,
    ...{ borderBottom: theme.predictionBorderBottom, width: '77px', justifyItems: 'left' }
}));

const StyledPredictionText = styled(TruncatedText)`
    whitespace: nowrap;
    min-width: 77px;
`;

const linkStyles = (textColor: string) =>
    classNamesFunction<ILinkProps, ILinkStyles>()({
        root: {
            color: textColor,
            '&:hover': { color: textColor },
            [HighContrastSelector]: important({ color: textColor })
        }
    });

const StyledCell = styled(TruncatedText)<{ backgroundColor: string }>(props => ({
    height: '40px',
    fontSize: '14px',
    minWidth: '72px',
    display: 'flex',
    alignItems: 'center',
    border: theme.border,
    justifyContent: 'center',
    backgroundColor: props.backgroundColor,
    msHighContrastAdjust: 'none',
    forcedColorAdjust: 'none'
}));

const ConfusionMatrixContainer = styled(Stack)({
    overflowX: 'auto',
    marginBottom: '15px'
});

const NoneAndNoneCellText = styled(LuisText)({
    color: theme.noneAndNoneTextColor
});

export const ModelConfusionMatrix = (props: ModelConfusionMatrixProps) => {
    const {
        onCellClick,
        mode = 'all',
        allViewIndices,
        onlyErrorsIndices,
        onlyMatchesIndices,
        modelConfusionMatrix,
        confusionMatrixCellColors,
        performanceMetricsBreakdown
    } = props;
    const { localize } = useLocalization();

    const metricsIndicesToShow = mode === 'errors' ? onlyErrorsIndices : mode === 'matches' ? onlyMatchesIndices : allViewIndices;

    const getCelTitle = (value: number, labeledName: string, predictedName: string) =>
        `${value}\n${localize(messages.labeledAs)}: ${labeledName}, ${localize(messages.predictedAs)}: ${predictedName}`;

    const handleOnCellClick = (index1: number, index2: number) => {
        onCellClick([performanceMetricsBreakdown[index1]?.name, performanceMetricsBreakdown[index2]?.name]);
    };

    const isNoneAndNoneCell = (index1: number, index2: number) =>
        index1 === index2 && performanceMetricsBreakdown[index1]?.name === LuisConstants.CustomText.NoneLabelInConfusionMatrix;

    return metricsIndicesToShow.length && modelConfusionMatrix.length ? (
        <ConfusionMatrixContainer tabIndex={0} role={'group'} aria-label={localize(messages.confusionMatrixAriaLabel)}>
            <StackItem tokens={{ padding: '0 0 0 140px' }}>
                <LuisText fontWeight={600}>{localize(messages.labeledAs)}</LuisText>
            </StackItem>

            <Stack horizontal verticalAlign="end" tokens={{ childrenGap: 60 }}>
                <LuisText fontWeight={600} styles={{ root: { minWidth: '80px', whiteSpace: 'nowrap' } }}>
                    {localize(messages.predictedAs)}
                </LuisText>

                <Stack horizontal styles={{ root: { marginLeft: '25px !important' } }}>
                    {performanceMetricsBreakdown.map(
                        (performanceMetric, index) =>
                            metricsIndicesToShow.includes(index) && (
                                <StyledLabel title={performanceMetric?.name} key={`${performanceMetric?.name}-${index}`}>
                                    <StyledLabelText>{performanceMetric?.name}</StyledLabelText>
                                </StyledLabel>
                            )
                    )}
                </Stack>
            </Stack>

            <Stack tokens={{ padding: '0 25px' }}>
                {metricsIndicesToShow.map(index => (
                    <Stack key={`${performanceMetricsBreakdown[index]?.name}-${index}`} horizontal verticalAlign="center">
                        <StyledPrediction title={performanceMetricsBreakdown[index]?.name}>
                            <StyledPredictionText>{performanceMetricsBreakdown[index]?.name}</StyledPredictionText>
                        </StyledPrediction>

                        {modelConfusionMatrix[index].map((val, idx) =>
                            metricsIndicesToShow.includes(idx) ? (
                                isNoneAndNoneCell(index, idx) ? (
                                    <StyledCell key={getId()} backgroundColor={theme.noneAndNoneCellColor}>
                                        <NoneAndNoneCellText>-</NoneAndNoneCellText>
                                    </StyledCell>
                                ) : (
                                        <StyledCell
                                            key={getId()}
                                            backgroundColor={confusionMatrixCellColors[index][idx]?.cellColor}
                                            >
                                            <ExternalLink
                                                role={'link'}
                                                onClick={() => handleOnCellClick(index, idx)}
                                                styles={linkStyles(confusionMatrixCellColors[index][idx]?.textColor)}
                                                aria-label={getCelTitle(
                                                    val,
                                                    performanceMetricsBreakdown[idx]?.name,
                                                    performanceMetricsBreakdown[index]?.name
                                                )}>
                                                {truncateValue(val, 3)}
                                            </ExternalLink>
                                        </StyledCell>
                                )
                            ) : null
                        )}
                    </Stack>
                ))}
            </Stack>
        </ConfusionMatrixContainer>
    ) : (
        <></>
    );
};
